// @@TECH_PACKS_v94@@ Client portal — Tech Packs tab.
// Data: /api/portal/techpacks* (server-scoped to this login's clients). The client
// reviews each print location (art, placement, Pantone inks) and either approves
// (checkbox + typed full name) or requests changes (comment + name). The server
// only accepts a decision on the revision shown here (409 `changed` otherwise).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { AuthedImg, useAuthedUrl, downloadAuthed, PlacementPreview, Swatch, fmtYmd, fmtTs, fmtBytes, num, blankText } from './TechPackParts';
import './techpacks.css';
// @@TECH_PACK_THUMBS_v97@@ portal previews use the cached sm/md variants
// @@TECH_PACK_FILE_REQUESTS_v98@@ no file/ZIP downloads for clients — “Request files” files a task for our team
// @@TECH_PACKS_v94_1@@ padded card bodies (.cp-card has no padding of its own)
// @@TECH_PACKS_DISCHARGE_v95@@ plain-language discharge note for clients (no internal warnings).

const BADGE = {
  review: ['Needs your review', '#fff4e0', '#9a5b00'],
  approved: ['Approved', '#e8f6ee', '#1f8a3c'],
  changes_requested: ['Changes requested', '#fdecec', '#b42318'],
};
function Badge({ status }) {
  const [label, bg, fg] = BADGE[status] || [status, '#f0f1f3', '#555'];
  return <span className="tpc-badge" style={{ background: bg, color: fg }}>{label}</span>;
}
const K = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--cp-muted)', fontWeight: 600, marginBottom: 4, display: 'block' };
async function readJson(r) { try { return await r.json(); } catch (e) { return {}; } }

export default function TechPacksTab({ clientId, onChanged }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState('');
  const autoOpened = useRef(false);
  const load = useCallback(() => {
    if (!clientId) return;
    apiFetch(`/api/portal/techpacks?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setList(d.packs || []);
        const waiting = (d.packs || []).filter((p) => p.status === 'review');
        if (!autoOpened.current && waiting.length === 1) setOpenId(waiting[0].id);
        autoOpened.current = true;
      })
      .catch(() => setList([]));
  }, [clientId]);
  useEffect(() => { setList(null); setOpenId(''); autoOpened.current = false; load(); }, [load]);

  if (openId) {
    return <PackView clientId={clientId} id={openId} onBack={() => { setOpenId(''); load(); }} onChanged={() => { load(); if (onChanged) onChanged(); }} />;
  }
  return (
    <div className="cp-card">
      <div className="cp-card-head"><h2>Tech packs</h2></div>
      <div className="tpc-body">
      <p className="tpc-intro">
        Print specs for your merch — artwork, placement, Pantone ink colors and the blank. Review each one and approve it before it goes to print.
      </p>
      {list === null && <div className="cp-empty">Loading…</div>}
      {list && list.length === 0 && <div className="cp-empty">No tech packs yet.</div>}
      {list && list.length > 0 && (
        <div className="tpc-grid">
          {list.map((p) => (
            <button type="button" key={p.id} className="tpc-card" onClick={() => setOpenId(p.id)}>
              <div className="tpc-thumb">
                {p.thumb_id
                  ? <AuthedImg path={`/api/portal/techpacks/${p.id}/files/${p.thumb_id}?thumb=sm&client_id=${encodeURIComponent(clientId)}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <span style={{ color: 'var(--cp-muted)', fontSize: 12 }}>No mockup</span>}
              </div>
              <div style={{ padding: '12px 14px', textAlign: 'left' }}>
                <div className="tp-mono" style={{ color: 'var(--cp-muted)', fontSize: 12 }}>{p.code} · Rev {p.revision}</div>
                <div style={{ fontWeight: 700, fontSize: 15, margin: '2px 0 6px' }}>{p.title}</div>
                <div style={{ color: 'var(--cp-muted)', fontSize: 13 }}>{[p.blank, p.blank_color].filter(Boolean).join(' · ') || '—'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 8 }}>
                  <Badge status={p.status} />
                  <span style={{ color: 'var(--cp-muted)', fontSize: 12 }}>{p.locations} location{p.locations === 1 ? '' : 's'}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function PackView({ clientId, id, onBack, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [reqOpen, setReqOpen] = useState(false);
  const q = `client_id=${encodeURIComponent(clientId)}`;
  const load = useCallback(() => {
    apiFetch(`/api/portal/techpacks/${id}?${q}`).then(async (r) => {
      const j = await readJson(r);
      if (r.ok) { setD(j); setErr(''); } else setErr(j.error || 'Could not load this tech pack.');
    }).catch(() => setErr('Could not load this tech pack.'));
  }, [id, q]);
  useEffect(() => { load(); }, [load]);
  async function dl(kind) {
    setBusy(kind);
    try { await downloadAuthed(`/api/portal/techpacks/${id}/${kind}?${q}`); } catch (e) { setErr(e.message); }
    setBusy('');
  }
  if (err && !d) return <div className="cp-card tpc-pad"><button className="cp-btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← All tech packs</button><div className="cp-empty">{err}</div></div>;
  if (!d) return <div className="cp-card tpc-pad"><div className="cp-empty">Loading…</div></div>;
  const p = d.pack;
  const info = [
    ['Blank', blankText(p.blank)], ['Garment color', p.blank.color], ['Fabric', p.blank.fabric], ['Sizes', p.sizes],
    ['Quantity', p.quantity != null ? Number(p.quantity).toLocaleString() + ' pcs' : null], ['Due date', p.due_date ? fmtYmd(p.due_date) : null],
    ['Print method', p.style_text], ['Your style #', p.client_style_code],
  ];
  return (
    <>
      <div className="cp-card tpc-pad">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <button className="cp-btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← All tech packs</button>
            <div className="tp-mono" style={{ color: 'var(--cp-muted)', fontSize: 13 }}>{p.code} · Revision {p.revision}</div>
            <h2 style={{ margin: '4px 0 8px', fontSize: 22 }}>{p.title}</h2>
            <Badge status={p.status} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="cp-btn-sm" disabled={!!busy} onClick={() => dl('pdf')}>{busy === 'pdf' ? 'Preparing…' : 'Download PDF'}</button>
            {p.files_request_only !== false && (p.mockups.length + p.references.length + p.locations.reduce((n, l) => n + l.files.length, 0)) > 0 && (
              <button className="cp-btn-sm" onClick={() => setReqOpen((o) => !o)} aria-expanded={reqOpen}>{reqOpen ? 'Close request' : 'Request files'}</button>
            )}
          </div>
        </div>
        {err && <div className="cp-alert" style={{ marginTop: 12 }}>{err}</div>}
        <div className="tpc-info">
          {info.filter(([, v]) => v).map(([k, v]) => <div key={k}><span style={K}>{k}</span><div style={{ fontSize: 14 }}>{v}</div></div>)}
        </div>
        {p.uses_discharge && (
          <div className="tpc-discharge">
            <strong>Discharge print.</strong> The ink removes the garment’s dye instead of sitting on top of it, so the print is soft and breathable.
            Colors can shift slightly between garment dye lots{p.discharge && p.discharge.strike_off ? ' — we’ll run a test print before production' : ''}.
            {p.discharge && p.discharge.result_color ? <> Expected color in discharged areas: <strong>{p.discharge.result_color}</strong>.</> : null}
          </div>
        )}
        {p.notes && <div className="tpc-section"><span style={K}>Notes</span><div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{p.notes}</div></div>}
        {p.mockups.length > 0 && (
          <div className="tpc-section">
            <span style={K}>Mockups</span>
            <div className="tpc-mockups">
              {p.mockups.map((m) => (
                <figure key={m.id} className="tpc-mock">
                  <div className="tpc-mock-frame"><AuthedImg path={`/api/portal/techpacks/${id}/files/${m.id}?thumb=sm&${q}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div>
                  <figcaption>{m.label || m.filename}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
      </div>

      {reqOpen && <FileRequest clientId={clientId} pack={p} onDone={() => { load(); }} onClose={() => setReqOpen(false)} />}
      {(d.file_requests || []).length > 0 && !reqOpen && (
        <div className="cp-alert" style={{ marginBottom: 20 }}>
          <strong>Files requested</strong> {fmtTs(d.file_requests[0].created_at)} by {d.file_requests[0].by} — we’ll send them over shortly.
          {d.file_requests.length > 1 ? ` (${d.file_requests.length} requests on this tech pack)` : ''}
        </div>
      )}

      {p.locations.map((l, i) => <LocationView key={l.id} packId={id} q={q} l={l} i={i} />)}

      <Respond clientId={clientId} pack={p} history={d.history} onDone={() => { load(); onChanged(); }} onStale={load} />
    </>
  );
}

function LocationView({ packId, q, l, i }) {
  const art = l.files.find((f) => f.is_image);
  const artUrl = useAuthedUrl(art ? `/api/portal/techpacks/${packId}/files/${art.id}?thumb=md&${q}` : '');
  return (
    <div className="cp-card tpc-pad">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <span className="tpc-num">{String(i + 1).padStart(2, '0')}</span>
        <h3 style={{ margin: 0, fontSize: 17 }}>{l.name}</h3>
      </div>
      <div className="tpc-loc">
        <div className="tpc-art">
          {artUrl ? <img src={artUrl} alt={l.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <span style={{ color: 'var(--cp-muted)', fontSize: 13 }}>{l.files.length ? 'Art attached (see files below)' : 'No artwork yet'}</span>}
        </div>
        <div className="tpc-diagram"><PlacementPreview loc={l} artUrl={artUrl} height={240} /></div>
      </div>
      <div className="tpc-info">
        <div><span style={K}>Print size</span>{l.width_in && l.height_in ? `${num(l.width_in)}″ W × ${num(l.height_in)}″ H` : '—'}</div>
        <div><span style={K}>Placement</span>{l.placement_text || '—'}</div>
        <div><span style={K}>Print style</span>{l.effective.text}</div>
        {l.grading && <div><span style={K}>Size grading</span>{l.grading}</div>}
        {l.placement_notes && <div><span style={K}>Placement notes</span>{l.placement_notes}</div>}
        {l.notes && <div><span style={K}>Notes</span>{l.notes}</div>}
      </div>
      <div className="tpc-section">
        <span style={K}>Ink colors (print order)</span>
        {l.colors.length === 0 && <div style={{ color: 'var(--cp-muted)', fontSize: 14 }}>—</div>}
        <div className="tpc-inks">
          {l.colors.map((c, k) => (
            <div key={k} className="tpc-ink">
              <Swatch hex={c.hex} size={30} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{c.pantone ? 'PMS ' + c.pantone : c.name || '—'}</div>
                <div style={{ color: 'var(--cp-muted)', fontSize: 12 }}>{[c.pantone ? c.name : null, c.underbase ? (c.discharge ? 'discharge underbase' : 'on underbase') : (c.discharge ? 'discharge ink' : null)].filter(Boolean).join(' · ') || ' '}</div>
              </div>
            </div>
          ))}
        </div>
        {l.colors.length > 0 && <div style={{ color: 'var(--cp-muted)', fontSize: 12, marginTop: 8 }}>Swatches are screen approximations — final ink is matched to the physical Pantone guide.</div>}
      </div>
      {l.files.length > 0 && (
        <div className="tpc-section" style={{ color: 'var(--cp-muted)', fontSize: 13 }}>
          Files: {l.files.map((f) => `${f.filename} (${fmtBytes(f.bytes)})`).join(', ')}
        </div>
      )}
    </div>
  );
}

// @@TECH_PACK_FILE_REQUESTS_v98@@ pick files (default: all) + optional note -> task for our team.
function FileRequest({ clientId, pack, onDone, onClose }) {
  const groups = [
    ...pack.locations.filter((l) => l.files.length).map((l) => ({ key: l.id, label: l.name, files: l.files })),
    ...(pack.mockups.length ? [{ key: 'mockups', label: 'Mockups', files: pack.mockups }] : []),
    ...(pack.references.length ? [{ key: 'refs', label: 'Reference files', files: pack.references }] : []),
  ];
  const all = groups.flatMap((g) => g.files.map((f) => f.id));
  const [sel, setSel] = useState(() => new Set(all));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [sent, setSent] = useState(false);
  const boxRef = useRef(null);
  // On phones the panel opens below a long header card — bring it into view.
  useEffect(() => { if (boxRef.current && boxRef.current.scrollIntoView) boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);
  const toggle = (id) => setSel((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setMsg('');
    const r = await apiFetch(`/api/portal/techpacks/${pack.id}/file-request`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: clientId, file_ids: sel.size === all.length ? [] : Array.from(sel), note }),
    });
    const d = await readJson(r);
    setBusy(false);
    if (!r.ok) { setMsg(d.error || 'Could not send your request.'); return; }
    setSent(true);
    setMsg(d.demo ? 'Demo account — nothing was sent.' : 'Request sent. Our team will get the files to you shortly.');
    onDone();
  }
  return (
    <div className="cp-card tpc-pad" ref={boxRef} style={{ scrollMarginTop: 80 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Request files</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--cp-muted)', fontSize: 14 }}>
        Original art files aren’t downloadable from the portal. Choose what you need and our team will send it to you.
      </p>
      {sent ? (
        <div>
          <div style={{ fontSize: 14, marginBottom: 12 }}>{msg}</div>
          <button type="button" className="cp-btn-sm" onClick={onClose}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            <input type="checkbox" checked={sel.size === all.length} onChange={(e) => setSel(e.target.checked ? new Set(all) : new Set())} />
            All files ({all.length})
          </label>
          <div className="tpc-req-groups">
            {groups.map((g) => (
              <div key={g.key} className="tpc-req-group">
                <div className="tpc-req-label">{g.label}</div>
                {g.files.map((f) => (
                  <label key={f.id} className="tpc-req-file">
                    <input type="checkbox" checked={sel.has(f.id)} onChange={() => toggle(f.id)} />
                    <span className="tpc-req-name">{f.label || f.filename}</span>
                    <span style={{ color: 'var(--cp-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtBytes(f.bytes)}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <label style={{ display: 'block', margin: '16px 0 14px' }}><span style={K}>Note (optional)</span>
            <textarea className="cp-input" style={{ width: '100%', minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit', padding: 10 }} value={note} maxLength={1000}
              placeholder="e.g. Vector files for our screen printer in Austin" onChange={(e) => setNote(e.target.value)} />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="cp-btn-sm" type="submit" disabled={busy || sel.size === 0}>{busy ? 'Sending…' : `Request ${sel.size === all.length ? 'all files' : sel.size + ' file' + (sel.size === 1 ? '' : 's')}`}</button>
            <button type="button" className="cp-btn-sm" style={{ background: 'transparent', color: 'var(--cp-txt)', border: '1px solid var(--cp-line-strong)' }} onClick={onClose}>Cancel</button>
            {msg && <span style={{ fontSize: 14 }}>{msg}</span>}
          </div>
        </form>
      )}
    </div>
  );
}

function Respond({ clientId, pack, history, onDone, onStale }) {
  const [mode, setMode] = useState('approve');
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setMsg('');
    const r = await apiFetch(`/api/portal/techpacks/${pack.id}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: clientId, decision: mode, name, agree, comment, revision: pack.revision }),
    });
    const d = await readJson(r);
    setBusy(false);
    if (r.ok) { setMsg(d.demo ? 'Demo account — nothing was sent.' : mode === 'approve' ? 'Thanks — approved.' : 'Thanks — we’ll make the changes and send a new revision.'); onDone(); return; }
    setMsg(d.error || 'Could not send your response.');
    if (d.code === 'changed' || d.code === 'not_open') onStale();
  }
  const past = history.filter((h) => h.note && ['approved', 'changes_requested'].includes(h.to_status));
  return (
    <div className="cp-card tpc-pad">
      {pack.can_respond ? (
        <form onSubmit={submit}>
          <h3 style={{ margin: '0 0 14px', fontSize: 17 }}>Your response</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <button type="button" className={mode === 'approve' ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => setMode('approve')}>Approve</button>
            <button type="button" className={mode === 'changes' ? 'cp-pill cp-pill-on' : 'cp-pill'} onClick={() => setMode('changes')}>Request changes</button>
          </div>
          {mode === 'approve' && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 4 }} />
              <span>I approve revision {pack.revision} of <strong>{pack.code}</strong> — artwork, placement, sizes and ink colors as shown — for production.</span>
            </label>
          )}
          {mode === 'changes' && (
            <label style={{ display: 'block', marginBottom: 12 }}><span style={K}>What should we change?</span>
              <textarea className="cp-input" style={{ width: '100%', minHeight: 100, boxSizing: 'border-box', fontFamily: 'inherit', padding: 10 }} value={comment} maxLength={4000} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Make the back print 2″ smaller and change the red to PMS 485 C" />
            </label>
          )}
          <label style={{ display: 'block', marginBottom: 14, maxWidth: 360 }}><span style={K}>Your full name</span>
            <input className="cp-input" style={{ width: '100%', boxSizing: 'border-box' }} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </label>
          <button className="cp-btn-sm" type="submit" disabled={busy || name.trim().length < 2 || (mode === 'approve' ? !agree : comment.trim().length < 3)}>
            {busy ? 'Sending…' : mode === 'approve' ? 'Approve tech pack' : 'Send change request'}</button>
          {msg && <div style={{ marginTop: 10, fontSize: 14 }}>{msg}</div>}
        </form>
      ) : (
        <div style={{ fontSize: 14 }}>
          {pack.status === 'approved' && <>Approved by <strong>{pack.decided_name}</strong> on {fmtTs(pack.decided_at)}.</>}
          {pack.status === 'changes_requested' && <>Changes requested by <strong>{pack.decided_name}</strong> on {fmtTs(pack.decided_at)}. We’ll send an updated revision.</>}
          {msg && <div style={{ marginTop: 8 }}>{msg}</div>}
        </div>
      )}
      {past.length > 0 && (
        <div className="tpc-section" style={{ borderTop: '1px solid var(--cp-line)', paddingTop: 16 }}>
          <span style={K}>Past responses</span>
          {past.map((h, k) => <div key={k} style={{ fontSize: 13, marginBottom: 8 }}><strong>Rev {h.revision}</strong> · {fmtTs(h.created_at)}<div style={{ whiteSpace: 'pre-wrap', color: 'var(--cp-muted)' }}>{h.note}</div></div>)}
        </div>
      )}
    </div>
  );
}
